/**
 * SearchPanel — find a sentence in this book, and jump to it.
 *
 * §5.8: *"In-book search rides `sentences.text` in FTS5 and lands in a later phase."* This is that
 * phase. It sits beside the chapter drawer as the reader's second piece of navigation, and like the
 * drawer it is the only way to reach a place forty swipes away.
 *
 * ── A result is a SENTENCE, and the jump is a `sid` ────────────────────────
 * Not a page number, not a percentage, not a character offset. §2.1: the sid is the only coordinate
 * system, and everything falls out of that — the result survives a font-size change, a re-narration
 * and a room; `Reader.goToSid` is the same function the follow pill and the chapter drawer land
 * through; and the sentence that comes back can be narrated, regenerated or reported without a
 * second lookup.
 *
 * ── LAW 1, on the wire and enforced here ──────────────────────────────────
 * Every hit carries `editionId` beside its `sid`, and a hit from a different edition than the one
 * mounted is **refused rather than resolved**. That is not defensive coding: a positional sid
 * resolved against a foreign edition does not fail, it silently addresses *a different sentence that
 * happens to exist*, and the reader would jump confidently to the wrong paragraph. The condition is
 * reachable — a book re-imported in another tab while this panel is open — and the answer is the
 * banner, not a jump.
 *
 * ── The index-health line, which is the point of the feature ──────────────
 * `sentences_fts` is external-content FTS5 with no triggers (P1), populated once per edition *outside*
 * the import transaction and logged rather than thrown on failure — deliberately, so a book stays
 * readable when indexing fails. The consequence is that a book can be perfectly readable and
 * completely unsearchable, and the only symptom is that search says "no results". So the server
 * reports `index: { state, indexed, total, detail }` on every response and this panel says it in
 * words. **"Nothing found" and "this book was never indexed" must never be the same screen**, or
 * search answers confidently with last month's book.
 *
 * ── Behaviour that is deliberate ───────────────────────────────────────────
 * · Debounced at 180 ms and the in-flight request is aborted, because a phone typing "Netherfield"
 *   would otherwise fire eleven queries at a box that is also streaming audio.
 * · Prefix matching while typing (`netherf` finds *Netherfield*), which is the server's default.
 * · Escape closes; the panel takes focus on open, so the arrow keys stop turning pages behind it —
 *   `usePageTurn`'s keyboard handler ignores events from form controls, not from a dialog.
 * · Jumping does **not** move the narration (LAW 12). Search is browsing; the follow pill is what
 *   offers the way back to the voice.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";

import { ApiError, api } from "../lib/api.ts";

// ─── the wire ────────────────────────────────────────────────────────────────

export type IndexState = "ready" | "partial" | "missing";

export interface SearchIndexHealth {
  state: IndexState;
  indexed: number;
  total: number;
  detail: string | null;
}

export interface SearchHit {
  sid: string;
  editionId: string;
  chapterOrd: number;
  chapterTitle: string;
  pageId: string | null;
  globalOrd: number;
  text: string;
  /** Half-open `[start, end)` character ranges into `text`, from SQLite's own tokenizer. */
  ranges: [number, number][];
  rank: number;
}

export interface SearchResponse {
  q: string;
  editionId: string | null;
  terms?: string[];
  hits: SearchHit[];
  truncated: boolean;
  index: SearchIndexHealth;
}

export interface SearchPanelProps {
  open: boolean;
  bookId: string;
  /** The edition the reader currently has mounted. LAW 1's other half. */
  editionId: string | null;
  onJump(sid: string): void;
  onClose(): void;
}

const DEBOUNCE_MS = 180;
/** Below two characters every book answers with itself, which is not a search result. */
const MIN_CHARS = 2;

export function SearchPanel(props: SearchPanelProps): ReactElement | null {
  const { open, bookId, editionId, onJump, onClose } = props;
  const [q, setQ] = useState("");
  const [result, setResult] = useState<SearchResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const restoreFocusTo = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        // `capture` and `stopPropagation` for the same reason the drawer does it: the reader's own
        // key handler is listening on the document and would otherwise turn a page behind the panel.
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      (restoreFocusTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open, onClose]);

  const trimmed = q.trim();

  useEffect(() => {
    if (!open) return;
    if (trimmed.length < MIN_CHARS) {
      setResult(null);
      setError(null);
      setBusy(false);
      return;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      setBusy(true);
      api
        .get<SearchResponse>(`/books/${encodeURIComponent(bookId)}/search?q=${encodeURIComponent(trimmed)}`, {
          signal: ac.signal,
        })
        .then((r) => {
          setResult(r);
          setError(null);
        })
        .catch((e: unknown) => {
          if (ac.signal.aborted) return;
          // A cancelled keystroke is not an error; anything else is, and it gets the server's own
          // sentence rather than "Search failed".
          setError(e instanceof ApiError ? e.message : "Search is not answering right now.");
          setResult(null);
        })
        .finally(() => {
          if (!ac.signal.aborted) setBusy(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [open, bookId, trimmed]);

  const jump = useCallback(
    (hit: SearchHit) => {
      onJump(hit.sid);
      onClose();
    },
    [onJump, onClose],
  );

  /**
   * LAW 1, enforced before anything is clickable.
   *
   * `result.editionId` is what the server searched; `editionId` is what this reader has mounted. If
   * they differ the book was re-imported under us, and every sid in the answer is an address in a
   * document this screen is not showing.
   */
  const foreign = result !== null && result.editionId !== null && editionId !== null && result.editionId !== editionId;

  const health = result?.index ?? null;
  const hits = foreign ? [] : (result?.hits ?? []);

  const summary = useMemo((): string => {
    if (trimmed.length < MIN_CHARS) return "Type at least two letters.";
    if (busy && result === null) return "Looking…";
    if (result === null) return "";
    if (foreign) return "";
    if (hits.length === 0) return health?.state === "ready" ? "Nothing in this book says that." : "";
    return `${hits.length}${result.truncated ? "+" : ""} ${hits.length === 1 ? "sentence" : "sentences"}`;
  }, [trimmed, busy, result, foreign, hits.length, health]);

  if (!open) return null;

  return (
    <>
      <div className="ab-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <div className="ab-sheet" role="dialog" aria-label="Search this book" aria-modal="true" tabIndex={-1} ref={panelRef}>
        <div className="ab-drawer-head">
          <span className="ab-drawer-title">Search this book</span>
          <button type="button" className="ab-btn" onClick={onClose} aria-label="Close search">
            Close
          </button>
        </div>

        {/* `.ab-search` styles its INPUT child (`.ab-search input`), which is where the 16px floor
            lives — anything smaller and Safari zooms the whole viewport on focus, and on a
            `position: fixed` document that leaves the page scrolled sideways with no way back. */}
        <div className="ab-search">
          <label className="ab-sr" htmlFor="ab-search-q">
            Search this book
          </label>
          <input
            id="ab-search-q"
            ref={inputRef}
            type="search"
            /* `search` rather than `text` so iOS offers the clear button, and no autocorrect: a
               proper noun is exactly what people search a book for, and autocorrect rewrites it. */
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="A word, a name, a phrase…"
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
          />
        </div>

        {/* The index-health line. Not a toast and not a console warning: it sits above the results,
            because it is the difference between "this book does not say that" and "we cannot answer
            for this book at all". */}
        {health && health.state !== "ready" && health.detail ? (
          <div className="ab-notice" role="status">
            <span>
              <strong>{health.state === "missing" ? "Search is not built for this book." : "Search is incomplete here."}</strong>{" "}
              {health.detail}
            </span>
          </div>
        ) : null}

        {foreign ? (
          <div className="ab-notice" role="alert">
            <span>
              This book was updated while the panel was open, so these results point into a different
              version of it. Close and reopen the book to search the one you are reading.
            </span>
          </div>
        ) : null}

        {error ? (
          <div className="ab-notice" role="alert">
            <span>{error}</span>
          </div>
        ) : null}

        <p className="ab-hint" aria-live="polite">
          {summary}
          {busy && result !== null ? " · updating" : ""}
        </p>

        <ul className="ab-drawer-list">
          {hits.map((hit) => (
            <li key={hit.sid}>
              {/* Deliberately NOT `.ab-chapter-name`: that class is `white-space: nowrap` with an
                  ellipsis, which is right for a chapter title and wrong for a sentence — it would
                  cut every result at the first line and hide the word that matched. */}
              <button type="button" className="ab-chapter-btn ab-search-row" onClick={() => jump(hit)}>
                <span className="ab-search-sentence">{marked(hit.text, hit.ranges)}</span>
                <span className="ab-chapter-mins">{hit.chapterTitle || `Chapter ${hit.chapterOrd + 1}`}</span>
              </button>
            </li>
          ))}
        </ul>

        {result?.truncated ? (
          <p className="ab-hint ab-hint-quiet">
            Only the closest matches are shown. Add another word to narrow it.
          </p>
        ) : null}
      </div>
    </>
  );
}

/**
 * The matched words, lit.
 *
 * The ranges come from SQLite's own `highlight()` — **the tokenizer that decided the row is a match
 * is the tokenizer that says where the match is**, so there is no second opinion about word
 * boundaries here and no client-side regex to disagree with FTS5 about what `don't` is. The server
 * discards the ranges entirely if they do not reconstruct the sentence exactly, so an empty array is
 * an honest "we could not place the highlight" and the sentence still shows, plain. Fail safe,
 * loudly: a search result that highlights the wrong word makes a reader think they misread.
 */
export function marked(text: string, ranges: readonly [number, number][]): ReactNode {
  if (ranges.length === 0) return text;
  const out: ReactNode[] = [];
  let at = 0;
  ranges.forEach(([start, end], i) => {
    if (start < at || end > text.length || end <= start) return;
    if (start > at) out.push(text.slice(at, start));
    out.push(
      <mark key={`m${i}`} className="ab-search-hit">
        {text.slice(start, end)}
      </mark>,
    );
    at = end;
  });
  if (at < text.length) out.push(text.slice(at));
  return out;
}
